import axios from "axios";
import config from "@/lib/config";
import { attachApiHeaders } from "@/lib/attachApiHeaders";

const API_URL = `${config.apiUrl}/income`;

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use(attachApiHeaders);

/**
 * Obtiene la lista de años con registros de ingresos.
 * Llama a: GET /income/monthly/years
 * @returns {Promise<Array<number>>}
 */
export const getMonthlyYears = async () => {
  try {
    const res = await api.get(`/monthly/years`);
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching monthly years:",
      error.response?.data || error.message
    );
    return [];
  }
};

/**
 * Obtiene un ingreso mensual por su ID.
 * Llama a: GET /income/monthly/:id
 * @param {string} id - El ID del registro de ingreso mensual.
 * @returns {Promise<object>}
 */
export const getMonthlyById = async (id) => {
  try {
    const res = await api.get(`/monthly/${id}`);
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching monthly income by ID:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Obtiene todos los ingresos mensuales para un año específico.
 * Llama a: GET /income/monthly/year/:year
 * @param {number} year - El año para el cual se solicitan los datos.
 * @returns {Promise<Array>}
 */
export const getMonthlyByYear = async (year) => {
  try {
    const res = await api.get(`/monthly/year/${year}`);
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching monthly incomes:",
      error.response?.data || error.message
    );
    return [];
  }
};

/**
 * Obtiene el ingreso anual consolidado para un año específico.
 * Llama a: GET /income/yearly/:year
 * @param {number} year - El año para el cual se solicita el dato.
 * @returns {Promise<object|null>}
 */
export const getYearlyByYear = async (year) => {
  try {
    const res = await api.get(`/yearly/${year}`);
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching yearly income:",
      error.response?.data || error.message
    );
    return null;
  }
};

/**
 * Crea o actualiza un registro de ingreso mensual.
 * Llama a: POST /income/monthly
 * @param {object} data - Los datos del ingreso mensual.
 * @returns {Promise<object>}
 */
export const upsertMonthly = async (data) => {
  try {
    const res = await api.post(`/monthly`, data);
    return res.data;
  } catch (error) {
    console.error(
      "Error upserting monthly income:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Crea o actualiza un registro de ingreso anual.
 * Llama a: POST /income/yearly
 * @param {object} data - Los datos del ingreso anual.
 * @returns {Promise<object>}
 */
export const upsertYearly = async (data) => {
  try {
    const res = await api.post(`/yearly`, data);
    return res.data;
  } catch (error) {
    console.error(
      "Error upserting yearly income:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Elimina un registro de ingreso mensual.
 * Llama a: DELETE /income/monthly/:id
 * @param {string} id - El ID del registro a eliminar.
 * @returns {Promise<object>}
 */
export const deleteMonthly = async (id) => {
  try {
    const res = await api.delete(`/monthly/${id}`);
    return res.data;
  } catch (error) {
    console.error(
      "Error deleting monthly income:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Elimina un registro de ingreso anual.
 * Llama a: DELETE /income/yearly/:id
 * @param {string} id - El ID del registro a eliminar.
 * @returns {Promise<object>}
 */
export const deleteYearly = async (id) => {
  try {
    const res = await api.delete(`/yearly/${id}`);
    return res.data;
  } catch (error) {
    console.error(
      "Error deleting yearly income:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Adjunta un rol de pago a un ingreso mensual.
 * Llama a: POST /income/attach-payment-role
 * @param {string} monthlyId - El ID del registro al que se adjunta el archivo.
 * @param {File} file - El archivo a subir.
 * @returns {Promise<object>}
 */
export const attachPaymentRole = async (monthlyId, file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    // Tu controller espera 'monthlyId' en el body del FormData
    formData.append("monthlyId", monthlyId);

    const res = await api.post(`/attach-payment-role`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data;
  } catch (error) {
    console.error(
      "Error attaching payment role:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Obtiene el reporte de ingresos para un año específico.
 * Llama a: GET /income/report/:year
 * @param {number} year - El año para el cual se solicita el reporte.
 * @returns {Promise<object>}
 */
export const getIncomeReport = async (year) => {
  try {
    const res = await api.get(`/monthly/report/${year}`, {
      headers: {
        "Content-Type": "application/json",
      },
    });
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching income report:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Obtiene las deducciones de seguro (insuranceDeduction) por mes para un año específico.
 * Llama a: GET /income/monthly/deductions/:year
 * @param {number} year - El año para el cual se solicitan las deducciones.
 * @returns {Promise<Array<{month: number, insuranceDeduction: number}>>}
 */
export const getDeductions = async (year) => {
  try {
    const res = await api.get(`/monthly/deductions/${year}`);
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching deductions:",
      error.response?.data || error.message
    );
    return [];
  }
};

export const getDeductionsTotal = async (year) => {
  try {
    const res = await api.get(`/total-deductions/${year}`);
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching total deductions:",
      error.response?.data || error.message
    );
    return null;
  }
};

/**
 * Obtiene el cálculo completo del Impuesto a Pagar para un año específico.
 * Llama a: GET /income/tax-to-pay/:year
 * @param {number} year - El año para el cual se solicita el cálculo del impuesto.
 * @returns {Promise<object>}
 */
export const getTaxToPay = async (year) => {
  try {
    const res = await api.get(`/tax-to-pay/${year}`);
    return res.data;
  } catch (error) {
    console.error(
      "Error fetching tax to pay calculation:",
      error.response?.data || error.message
    );
    throw error;
  }
};
